import { DateFieldUI } from '@axo/form/feature/form-input/DateField/DateFieldUI';
import { DropdownFieldUI } from '@axo/form/feature/form-input/DropdownField/DropdownFieldUI';
import { NumberFieldUI } from '@axo/form/feature/form-input/NumberField/NumberFieldUI';
import { Residence } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { Grid } from '@axo/ui-core/components/layout/item';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { formatSpacedNumber } from '../../../../../../utils/format';
import { housingOptions } from '../../../config/globals';
import { gridColumns } from '../../../config/layouts/grid';
import { HousingFormGroupData } from '../../../FormData.no.types';
import styles from '../../../loan.module.scss';
import {
  extraIncomeRentalFieldOptions,
  housingDebtOutstandingAmountFieldOption,
  housingTypeFieldOptions,
  livedAtAddressSinceFieldOption,
  rentCostMonthlyFieldOptions,
} from './config';

export const HousingFormGroup = () => {
  const { t } = useTranslation();

  const { watch } = useFormContext();

  const currentYear = new Date().getFullYear();

  const housingType = watch('LoanApplication/HousingType');

  const { askExtraRentalIncome, askRentCostMonthly, askHousingDebt } =
    useMemo(() => {
      const askExtraRentalIncome = [
        Residence.CooperativeApartment,
        Residence.OwnApartment,
        Residence.OwnHouse,
      ].includes(housingType);

      const askRentCostMonthly = [
        Residence.Rental,
        Residence.CooperativeApartment,
        Residence.OwnApartment,
      ].includes(housingType);

      const askHousingDebt = [
        Residence.CooperativeApartment,
        Residence.OwnApartment,
        Residence.OwnHouse,
      ].includes(housingType);

      return {
        askExtraRentalIncome,
        askRentCostMonthly,
        askHousingDebt,
      };
    }, [housingType]);

  return (
    <div className={styles.inputGroup} data-grey={true}>
      <Grid columns={gridColumns}>
        <DropdownFieldUI<HousingFormGroupData>
          name="LoanApplication/HousingType"
          label={t('HousingType.label')}
          data-testid="housing-dropdown"
          options={housingOptions}
          {...housingTypeFieldOptions}
        />

        <DateFieldUI<HousingFormGroupData>
          name="Person/LivedAtAddressSince"
          label={t('LivedAtAddressSince.label')}
          data-testid="lived-at-address-since-dropdown"
          minYear={currentYear - 10}
          maxYear={currentYear}
          minYearSuffix={t('LivedAtAddressSince.year.suffix')}
          yearPlaceholder={t('LivedAtAddressSince.placeholder')}
          i18n="no-NO"
          showYearOnly
          latestYearAtTop
          disableDatesInFuture
          rules={livedAtAddressSinceFieldOption.registerOptions}
        />

        {askExtraRentalIncome && (
          <NumberFieldUI<HousingFormGroupData>
            name="LoanApplication/ExtraIncomeRental"
            label={t('ExtraIncomeRental.label')}
            data-testid="extra-income-rental-input"
            format={formatSpacedNumber}
            suffix={t('suffix.currency')}
            {...extraIncomeRentalFieldOptions}
          />
        )}

        {askRentCostMonthly && (
          <NumberFieldUI<HousingFormGroupData>
            name="LoanApplication/RentCostMonthly"
            label={t('RentCostMonthly.label', { context: housingType })}
            data-testid="rent-cost-input"
            format={formatSpacedNumber}
            suffix={t('suffix.currency')}
            {...rentCostMonthlyFieldOptions}
          />
        )}

        {askHousingDebt && (
          <NumberFieldUI<HousingFormGroupData>
            name="Debt/Housing/OutstandingAmount"
            label={t('Debt.Housing.OutstandingAmount.label')}
            data-testid="housing-outstanding-amount-input"
            format={formatSpacedNumber}
            suffix={t('suffix.currency')}
            {...housingDebtOutstandingAmountFieldOption}
          />
        )}
      </Grid>
    </div>
  );
};
